import React, { useState, useEffect, useRef, useCallback } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import Spinner from "../../../common/Spinner";
import StackedMultiLineChart from "../../../common/StackedMultiLineChart";
import CircuitsTableView from "../circuitsTable";
import apiService from "../../../config/api-config";
import { getTimestampFromUTCString } from "../../../utils/displayTime";
import { chartPrimaryColors } from "../../../utils/colors";
import { REPORT_NAMES } from "../../../utils/enums";
import { useI18n } from "../../../i18n/useI18n";
const [HbrCard] = reactWrapper(["hbr-card"]);

const chartHeight = "250px";

const View = ({ globalV4PayloadReport, siteNameSet, min, max }) => {
  const { tViews } = useI18n("views");
  const [latency, setLatencyData] = useState([]);
  const [loss_percentage, setLossData] = useState([]);
  const [jitter, setJitterData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [selections, setSelections] = useState({});
  const [lljLoaded, setLljLoaded] = useState(false);
  const [tableLoaded, setTableLoaded] = useState(false);
  const [isSetAllSites, setAllSites] = useState(false);
  const networkCharts = [
    {
      title: tViews("circuit.loss"),
      valueY: loss_percentage,
      unit: tViews("circuit.percentage"),
    },
    {
      title: tViews("circuit.latency"),
      valueY: latency,
      unit: tViews("circuit.ms"),
    },
    {
      title: tViews("circuit.jitter"),
      valueY: jitter,
      unit: tViews("circuit.ms"),
    },
  ];
  const previewRef = useRef(null);
  const getAppData = useCallback(async (payload) => {
    setLoader(true);
    const result = await apiService.getSLAReportWidget(payload);
    const reportRows = result?.data?.data;
    const entryDataLatency = {};
    const entryDataLoss = {};
    const entryDataJitter = {};

    const visibleCircuits = {};
    if (reportRows?.length > 0) {
      for (const {
        circuit_name,
        entry_ts,
        latency,
        loss_percentage,
        jitter,
        bandwidth_rank,
      } of reportRows) {
        const date = getTimestampFromUTCString(entry_ts);
        visibleCircuits[circuit_name] = bandwidth_rank;
        if (Number.isFinite(latency)) {
          if (entryDataLatency[date] instanceof Object)
            entryDataLatency[date][circuit_name] = latency;
          else entryDataLatency[date] = { date, [circuit_name]: latency };
        }
        if (Number.isFinite(loss_percentage)) {
          if (entryDataLoss[date] instanceof Object)
            entryDataLoss[date][circuit_name] = loss_percentage;
          else entryDataLoss[date] = { date, [circuit_name]: loss_percentage };
        }
        if (Number.isFinite(jitter)) {
          if (entryDataJitter[date] instanceof Object)
            entryDataJitter[date][circuit_name] = jitter;
          else entryDataJitter[date] = { date, [circuit_name]: jitter };
        }
      }
      let selections = Object.keys(visibleCircuits);
      let bandwidthColor = {};
      selections.forEach((item, appIndex) => {
        bandwidthColor[item] = chartPrimaryColors[appIndex];
      });
      setSelections(bandwidthColor);
      setLatencyData(Object.values(entryDataLatency));
      setLossData(Object.values(entryDataLoss));
      setJitterData(Object.values(entryDataJitter));
      setLoader(false);
      setLljLoaded(true);
    } else {
      setLoader(false);
      setLljLoaded(true);
    }
  }, []);

  const isreportCallback = (data) => {
    setTableLoaded(data);
  };

  useEffect(() => {
    if (
      lljLoaded &&
      tableLoaded &&
      previewRef.current &&
      (siteNameSet || isSetAllSites)
    ) {
      previewRef.current.id = "isLoaded_chart";
    }
  }, [lljLoaded, tableLoaded, siteNameSet, isSetAllSites]);

  useEffect(() => {
    let payload = {
      ...globalV4PayloadReport,
      sort: {
        entry_ts: "asc",
        loss_rank: "asc",
      },
      loss_rank: [1, 2, 3, 4, 5],
    };
    getAppData(payload);
    if (globalV4PayloadReport["site_id"] == undefined) {
      setAllSites(true);
    }
  }, []);

  return (
    <div className="report-content" ref={previewRef}>
      <div className="hbr-css__layout-col-lg">
        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[0].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[0].title}
                data={networkCharts[0].valueY}
                areaChart={false}
                reporting={true}
                colorset={selections}
                label={networkCharts[0].unit}
                stacked={false}
                height={chartHeight}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>

        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[1].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[1].title}
                data={networkCharts[1].valueY}
                areaChart={false}
                reporting={true}
                colorset={selections}
                label={networkCharts[1].unit}
                stacked={false}
                height={chartHeight}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>

        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[2].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[2].title}
                data={networkCharts[2].valueY}
                areaChart={false}
                reporting={true}
                colorset={selections}
                label={networkCharts[2].unit}
                stacked={false}
                height={chartHeight}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>
        <CircuitsTableView
          globalV4PayloadReport={globalV4PayloadReport}
          reportName={REPORT_NAMES.REPORT_LINK_SLA}
          isreportCallback={isreportCallback}
        />
      </div>
    </div>
  );
};

View.propTypes = {
  globalV4PayloadReport: PropTypes.object.isRequired,
  siteNameSet: PropTypes.bool,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default View;
